/**
 * Dashboard Component
 *
 * Main dashboard page displaying bot overview, system metrics, and quick actions.
 * Provides at-a-glance status of all bots and key platform metrics.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Bot status enum matching backend
 */
type BotStatus = 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING' | 'ERROR';

/**
 * Bot summary for dashboard display
 */
interface BotSummary {
  id: string;
  name: string;
  status: BotStatus;
  lastStarted: string | null;
  description: string | null;
}

/**
 * Dashboard statistics
 */
interface DashboardStats {
  totalBots: number;
  runningBots: number;
  stoppedBots: number;
  errorBots: number;
  totalTokensUsed: number;
  totalCost: number;
}

/**
 * Recent activity item
 */
interface ActivityItem {
  id: string;
  type: 'bot_started' | 'bot_stopped' | 'bot_error' | 'config_changed';
  botName: string;
  message: string;
  timestamp: string;
}

/**
 * API response types
 */
interface BotsResponse {
  bots: BotSummary[];
  total: number;
}

interface StatsResponse {
  stats: DashboardStats;
}

interface ActivityResponse {
  activities: ActivityItem[];
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<BotStatus, { bg: string; text: string; dot: string }> = {
  RUNNING: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  STOPPED: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
  STARTING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  STOPPING: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  ERROR: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
};

const ACTIVITY_ICONS: Record<ActivityItem['type'], React.ReactNode> = {
  bot_started: (
    <svg className="h-4 w-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  bot_stopped: (
    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  ),
  bot_error: (
    <svg className="h-4 w-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  config_changed: (
    <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a timestamp for display
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format large numbers with abbreviations
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Statistics card component
 */
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: 'default' | 'green' | 'red' | 'yellow' | 'blue';
}

function StatCard({ title, value, icon, trend, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'text-white',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
  };

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <p className={`mt-2 text-3xl font-bold ${colorClasses[color]}`}>
            {value}
          </p>
          {trend && (
            <p
              className={`mt-1 text-sm ${
                trend.isPositive ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {trend.isPositive ? '+' : '-'}{Math.abs(trend.value)}% from last week
            </p>
          )}
        </div>
        <div className="rounded-full bg-discord-light p-3 text-gray-400">
          {icon}
        </div>
      </div>
    </div>
  );
}

/**
 * Bot status card component
 */
interface BotCardProps {
  bot: BotSummary;
}

function BotCard({ bot }: BotCardProps) {
  const statusColor = STATUS_COLORS[bot.status];

  return (
    <Link
      to={`/bots/${bot.id}/config`}
      className="block rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate">{bot.name}</h3>
          {bot.description && (
            <p className="mt-1 text-sm text-gray-400 truncate">{bot.description}</p>
          )}
        </div>
        <div
          className={`ml-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${statusColor.bg} ${statusColor.text}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusColor.dot}`} />
          {bot.status}
        </div>
      </div>
      <div className="mt-3 text-xs text-gray-500">
        Last active: {formatTimestamp(bot.lastStarted)}
      </div>
    </Link>
  );
}

/**
 * Activity item component
 */
interface ActivityItemProps {
  activity: ActivityItem;
}

function ActivityItemComponent({ activity }: ActivityItemProps) {
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="flex-shrink-0 mt-0.5">
        {ACTIVITY_ICONS[activity.type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white">
          <span className="font-medium">{activity.botName}</span>{' '}
          <span className="text-gray-400">{activity.message}</span>
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {formatTimestamp(activity.timestamp)}
        </p>
      </div>
    </div>
  );
}

/**
 * Empty state component
 */
interface EmptyStateProps {
  title: string;
  description: string;
  action?: {
    label: string;
    href: string;
  };
}

function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      {action && (
        <Link
          to={action.href}
          className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-6">
            <div className="h-4 w-24 bg-discord-light rounded mb-3" />
            <div className="h-8 w-16 bg-discord-light rounded" />
          </div>
        ))}
      </div>

      {/* Content skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg bg-discord-darker border border-discord-light p-6">
          <div className="h-6 w-32 bg-discord-light rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-discord-light rounded" />
            ))}
          </div>
        </div>
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
          <div className="h-6 w-32 bg-discord-light rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-discord-light rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

const BotIcon = () => (
  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const PlayIcon = () => (
  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const TokenIcon = () => (
  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
    />
  </svg>
);

const CurrencyIcon = () => (
  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// =============================================================================
// Main Component
// =============================================================================

/**
 * Dashboard page component
 *
 * Displays:
 * - Overview statistics (total bots, running bots, token usage, costs)
 * - List of bots with status indicators
 * - Recent activity feed
 * - Quick actions for common tasks
 */
export function Dashboard() {
  const { user } = useAuth();
  const { currentTenant, effectiveTenantId, isGlobalAdmin } = useTenant();

  const [bots, setBots] = useState<BotSummary[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch dashboard data
   */
  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      // Build query params for tenant scoping
      const queryParams = effectiveTenantId
        ? `?tenantId=${effectiveTenantId}`
        : '';

      // Fetch all data in parallel
      const [botsResult, statsResult, activityResult] = await Promise.all([
        apiClient.get<BotsResponse>(`/bots${queryParams}`),
        apiClient.get<StatsResponse>(`/dashboard/stats${queryParams}`),
        apiClient.get<ActivityResponse>(`/dashboard/activity${queryParams}`),
      ]);

      // Handle bots response
      if (isSuccess(botsResult)) {
        setBots(botsResult.data.bots);
      } else {
        // If bots endpoint fails, use empty array (might not be implemented yet)
        setBots([]);
      }

      // Handle stats response
      if (isSuccess(statsResult)) {
        setStats(statsResult.data.stats);
      } else {
        // Use default stats if endpoint not available
        setStats({
          totalBots: bots.length,
          runningBots: bots.filter((b) => b.status === 'RUNNING').length,
          stoppedBots: bots.filter((b) => b.status === 'STOPPED').length,
          errorBots: bots.filter((b) => b.status === 'ERROR').length,
          totalTokensUsed: 0,
          totalCost: 0,
        });
      }

      // Handle activity response
      if (isSuccess(activityResult)) {
        setActivities(activityResult.data.activities);
      } else {
        // Use empty activities if endpoint not available
        setActivities([]);
      }
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveTenantId]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchDashboardData();
  }, [fetchDashboardData]);

  /**
   * Calculate computed stats from bots if stats not available
   */
  const computedStats = useMemo<DashboardStats>(() => {
    if (stats) return stats;

    return {
      totalBots: bots.length,
      runningBots: bots.filter((b) => b.status === 'RUNNING').length,
      stoppedBots: bots.filter((b) => b.status === 'STOPPED').length,
      errorBots: bots.filter((b) => b.status === 'ERROR').length,
      totalTokensUsed: 0,
      totalCost: 0,
    };
  }, [stats, bots]);

  /**
   * Get greeting based on time of day
   */
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  /**
   * Get user's first name for greeting
   */
  const firstName = useMemo(() => {
    if (!user?.name) return '';
    return user.name.split(' ')[0];
  }, [user?.name]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {greeting}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="mt-1 text-gray-400">
              {currentTenant
                ? `Managing ${currentTenant.name}`
                : isGlobalAdmin
                  ? 'Viewing all tenants'
                  : 'Welcome to DiscordMinstrel'}
            </p>
          </div>
          <Link
            to="/bots"
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Bot
          </Link>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => void fetchDashboardData()}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Total Bots"
                value={computedStats.totalBots}
                icon={<BotIcon />}
              />
              <StatCard
                title="Running"
                value={computedStats.runningBots}
                icon={<PlayIcon />}
                color="green"
              />
              <StatCard
                title="Tokens Used"
                value={formatNumber(computedStats.totalTokensUsed)}
                icon={<TokenIcon />}
                color="blue"
              />
              <StatCard
                title="Total Cost"
                value={formatCurrency(computedStats.totalCost)}
                icon={<CurrencyIcon />}
              />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Bots Section */}
              <div className="lg:col-span-2">
                <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-white">Your Bots</h2>
                    <Link
                      to="/bots"
                      className="text-sm text-discord-blurple hover:underline"
                    >
                      View all
                    </Link>
                  </div>

                  {bots.length === 0 ? (
                    <EmptyState
                      title="No bots yet"
                      description="Create your first bot to get started with DiscordMinstrel"
                      action={{ label: 'Create Bot', href: '/bots' }}
                    />
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {bots.slice(0, 4).map((bot) => (
                        <BotCard key={bot.id} bot={bot} />
                      ))}
                    </div>
                  )}

                  {bots.length > 4 && (
                    <div className="mt-4 text-center">
                      <Link
                        to="/bots"
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        +{bots.length - 4} more bots
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Activity Feed */}
              <div>
                <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">
                    Recent Activity
                  </h2>

                  {activities.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-400">No recent activity</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-discord-light">
                      {activities.slice(0, 5).map((activity) => (
                        <ActivityItemComponent
                          key={activity.id}
                          activity={activity}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-6 rounded-lg bg-discord-darker border border-discord-light p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">
                    Quick Actions
                  </h2>
                  <div className="space-y-2">
                    <Link
                      to="/bots"
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-discord-light hover:text-white transition-colors"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create new bot
                    </Link>
                    <Link
                      to="/voices"
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-discord-light hover:text-white transition-colors"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Manage voice profiles
                    </Link>
                    <Link
                      to="/reports/costs"
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-discord-light hover:text-white transition-colors"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View cost reports
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default Dashboard;
